<template>
  <div v-if="visible" class="alternate-values-editor mt-1">
    <div class="flex flex-wrap gap-1 items-center">
      <el-tag
        v-for="(tag, index) in localValues"
        :key="index"
        :closable="!readonly"
        size="small"
        type="info"
        @close="removeTag(index)"
      >
        {{ tag }}
      </el-tag>
      <!-- HA-3092: dormant alternates (flag off) are shown, not editable. -->
      <span
        v-if="readonly"
        class="text-xs text-gray-500"
        data-test="alternate-values-inactive"
      >Inactive: not learned from imports while alternate import values are off</span>
      <template v-else>
        <el-input
          v-if="inputVisible"
          ref="chipInput"
          v-model="inputValue"
          size="mini"
          class="alternate-input"
          placeholder="Add synonym"
          @keyup.enter.native="handleInputConfirm"
          @blur="handleInputConfirm"
        />
        <el-button
          v-else
          size="mini"
          type="text"
          @click="showInput"
        >
          + Add
        </el-button>
      </template>
    </div>
    <p v-if="validationError" class="text-red-600 text-xs mt-1">
      {{ validationError }}
    </p>
  </div>
</template>

<script>
import { normalizeValue, findIntroducedConflicts } from '@/utilities/optionCustomLists/alternateImportValues'

export default {
  name: 'AlternateValuesChipEditor',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    visible: {
      type: Boolean,
      default: false
    },
    // HA-3092: show the values without letting them be changed (flag off).
    readonly: {
      type: Boolean,
      default: false
    },
    optionId: {
      type: String,
      default: ''
    },
    optionLabel: {
      type: String,
      default: ''
    },
    allOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      inputVisible: false,
      inputValue: '',
      validationError: ''
    }
  },
  computed: {
    localValues: {
      get() {
        return Array.isArray(this.value) ? this.value : []
      },
      set(val) {
        this.$emit('input', val)
      }
    }
  },
  methods: {
    showInput() {
      this.inputVisible = true
      this.$nextTick(() => {
        if (this.$refs.chipInput) {
          this.$refs.chipInput.focus()
        }
      })
    },
    handleInputConfirm() {
      const val = (this.inputValue || '').trim()
      if (!val) {
        this.inputVisible = false
        this.inputValue = ''
        this.validationError = ''
        return
      }

      // Pre-validate: check if adding this value would cause a conflict
      const conflict = this.validateNewValue(val)
      if (conflict) {
        this.validationError = conflict
        return
      }

      const newValues = [...this.localValues, val]
      this.$emit('input', newValues)
      this.inputVisible = false
      this.inputValue = ''
      this.validationError = ''
    },
    removeTag(index) {
      if (this.readonly) return
      const newValues = [...this.localValues]
      newValues.splice(index, 1)
      this.$emit('input', newValues)
    },
    validateNewValue(val) {
      const normalized = normalizeValue(val)

      // Check against own label
      if (normalized === normalizeValue(this.optionLabel)) {
        return `"${val}" matches this option's own label.`
      }

      // Check against existing synonyms on this option
      for (const existing of this.localValues) {
        if (normalizeValue(existing) === normalized) {
          return `"${val}" is already added.`
        }
      }

      // Build a simulated after-state and check for conflicts
      const afterOptions = this.allOptions.map(opt => {
        if (opt.id === this.optionId) {
          return {
            ...opt,
            alternateImportValues: [...this.localValues, val]
          }
        }
        return opt
      })

      const conflicts = findIntroducedConflicts(
        this.allOptions,
        afterOptions,
        true
      )

      if (conflicts.length > 0) {
        const c = conflicts[0]
        return `"${val}" conflicts with "${c.occurrences.find(o => o.optionId !== this.optionId)?.rawValue || c.normalizedValue}" (${c.kind.replace(/-/g, ' ')}).`
      }

      return null
    }
  }
}
</script>

<style scoped>
.alternate-values-editor {
  padding-left: 0.25rem;
}
.alternate-input {
  width: 120px;
}
</style>
